import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import EventComment from "./EventComment";
import { useUserRefresh } from "../user/UserProvider";
import { IMAGE } from "../../utilities/constants/MediaType";
import {
  EventCommentModel,
  EventModel,
} from "../../utilities/models/EventModel";
import CustomToast from "../../utilities/components/CustomToast";
import {
  createEventComment,
  getCommentsByEventId,
  getEventById,
  getMediaByEventId,
  toggleReaction,
} from "./EventServices";
import { Carousel } from "react-responsive-carousel";
import KeycloakService from "../config/KeycloakService";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { ThumbUp, ThumbDown } from "../../utilities/components/Thumbs";
import { abbreviateNumber } from "../../utilities/functions/Helpers";
import LocalizedModal from "../../utilities/components/LocalizedModal";
import Modal from "../../utilities/components/Modal";
import UseEventLikesDislikesStatus from "./UseEventLikesDislikesStatus";
import { ToastContainer } from "react-toastify";

/**
 * A card that displays an event.
 * @param {object} event The event to be displayed.
 * @returns An event card element.
 */
const EventCard = () => {
  const { id } = useParams();
  const [eventMedia, setEventMedia] = useState([]);
  const setFetchUserFlag = useUserRefresh();
  const [event, setEvent] = useState(EventModel);
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [comment, setComment] = useState(EventCommentModel); //New comment posted by user
  const [showComments, setShowComments] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [comments, setComments] = useState([]);
  const [showLikeDialog, setShowLikeDialog] = useState(false);
  const [showDislikeDialog, setShowDislikeDialog] = useState(false);
  const {
    likesDislikesStates: { likes, dislikes, thisUserLiked, thisUserDisliked },
  } = UseEventLikesDislikesStatus(event);

  const likeAndDislikeMessage = "Log in to make your opinion count.";

  const singIn = async () => {
    await KeycloakService.doLogin(setFetchUserFlag);
  };

  const toggleActionButtons = async () => {
    if (!KeycloakService.isLoggedIn()) {
      await singIn();
    }
    setShowActionButton(true);
  };

  const commentChangeHandler = (e) => {
    setComment({ ...comment, content: e.target.value });
  };

  const cancelComment = () => {
    setComment(EventCommentModel);
    setShowActionButton((prev) => !prev);
  };

  const submitComment = async () => {
    if (comment.content.length > 2000) {
      CustomToast("Comment must be less than 2000 characters.", "error");
      return;
    }
    const newComment = { ...comment };
    newComment.eventId = event.id;
    newComment.user = {
      userId: KeycloakService.getUserId(),
      firstName: KeycloakService.getUserFirstName(),
      lastName: KeycloakService.getUserLastName(),
    };
    newComment.postedDate = new Date();
    await createEventComment(newComment, setRefresh);
    setComment(EventCommentModel);
  };

  const toggleLike = async () => {
    if (KeycloakService.isLoggedIn()) {
      // Optimistic update can be tricky with toggles, so we relied on refresh from backend
      // But for better UX we might want to do it. For now, let's call API and refresh.
      await toggleReaction(
        KeycloakService.getUserId(),
        "EVENT",
        event.id,
        "LIKE",
        setRefresh,
      );
    } else {
      setShowDislikeDialog(false);
      setShowLikeDialog(true);
    }
  };

  const toggleDislike = async () => {
    if (KeycloakService.isLoggedIn()) {
      await toggleReaction(
        KeycloakService.getUserId(),
        "EVENT",
        event.id,
        "DISLIKE",
        setRefresh,
      );
    } else {
      setShowLikeDialog(false);
      setShowDislikeDialog(true);
    }
  };

  useEffect(() => {
    getEventById(setEvent, id);
    getMediaByEventId(setEventMedia, id);
    getCommentsByEventId(setComments, id);
  }, [id, refresh]);

  return (
    <div className="min-h-screen w-full bg-slate-50 dark:bg-slate-900 py-6 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Media / Hero Section */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl overflow-hidden ring-1 ring-slate-900/5 dark:ring-white/10">
          <div className="relative w-full aspect-video bg-slate-200 dark:bg-slate-700">
            {/* Carousel Container - Absolutely positioned to strictly fill the aspect ratio box */}
            <div className="absolute inset-0 z-0">
              <Carousel
                showThumbs={false}
                showStatus={false}
                infiniteLoop={true}
                useKeyboardArrows={true}
                swipeable={true}
                emulateTouch={true}
                className="h-full w-full event-hero-carousel"
                renderItem={(item) => (
                  <div className="h-full w-full">{item}</div>
                )}
              >
                {eventMedia.map((media) => {
                  const mediaType = media?.sharedMedia?.type || media?.type;
                  const mediaUrl =
                    media?.sharedMedia?.mediaUrl || media?.mediaUrl;
                  const isImage = mediaType?.startsWith(IMAGE);

                  return (
                    <div
                      key={`media-${media.id}`}
                      className="w-full h-full bg-slate-100 dark:bg-slate-900 flex items-center justify-center"
                    >
                      {isImage ? (
                        <div className="w-full h-full relative overflow-hidden">
                          {/* Blurred Background for premium feel */}
                          <div
                            className="absolute inset-0 bg-cover bg-center blur-md scale-110 opacity-50 dark:opacity-30"
                            style={{
                              backgroundImage: `url(${mediaUrl ?? "assets/images/image_placeholder_icon.png"})`,
                            }}
                          />
                          {/* Main Image */}
                          <img
                            src={
                              mediaUrl ??
                              "assets/images/image_placeholder_icon.png"
                            }
                            alt={event.title}
                            className="relative w-full h-full object-contain z-10"
                          />
                        </div>
                      ) : (
                        <video
                          controls
                          className="w-full h-full object-contain bg-black"
                        >
                          <source src={mediaUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  );
                })}
              </Carousel>
            </div>
          </div>

          {/* Event Content Info */}
          <div className="relative z-10 px-6 py-8 sm:px-10 bg-white dark:bg-slate-800">
            {/* Header: Title & Meta */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8 border-b border-gray-100 dark:border-slate-700 pb-8">
              <div className="flex-1 space-y-4">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
                  {event.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-slate-100 dark:bg-slate-700/50 rounded-full">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {event.postedDate
                      ? new Date(event.postedDate).toLocaleDateString(
                          undefined,
                          {
                            dateStyle: "long",
                          },
                        )
                      : "Date TBD"}
                  </span>
                  {/* You could add location or other meta tags here */}
                </div>
              </div>

              {/* Actions: Likes/Dislikes */}
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleLike}
                  className={`group flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 active:scale-95 ${
                    thisUserLiked
                      ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                  }`}
                >
                  <ThumbUp
                    filled={thisUserLiked}
                    className={thisUserLiked ? "fill-white" : "fill-current"}
                  />
                  <span>{likes > 0 ? abbreviateNumber(likes) : "Like"}</span>
                </button>

                <button
                  onClick={toggleDislike}
                  className={`group flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 active:scale-95 ${
                    thisUserDisliked
                      ? "bg-red-500 text-white shadow-md shadow-red-500/20"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                  }`}
                >
                  <ThumbDown
                    filled={thisUserDisliked}
                    className={thisUserDisliked ? "fill-white" : "fill-current"}
                  />
                  {dislikes > 0 && <span>{abbreviateNumber(dislikes)}</span>}
                </button>
              </div>
            </div>

            {/* Description */}
            <div className="prose prose-slate dark:prose-invert max-w-none mb-12">
              <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-line">
                {event.description}
              </p>
            </div>

            {/* Comments Section */}
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  Comments
                  <span className="px-2.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm rounded-full">
                    {abbreviateNumber(event?.commentsCount ?? 0)}
                  </span>
                </h3>
                {/* Sort Button placeholder - implement logic if needed */}
                <button className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors">
                  Sort by Newest
                </button>
              </div>

              {/* Add Comment Input */}
              <div className="bg-slate-50 dark:bg-slate-900/50 p-1 rounded-2xl ring-1 ring-slate-200 dark:ring-slate-700 focus-within:ring-2 focus-within:ring-blue-500 transition-shadow">
                <div className="bg-white dark:bg-slate-800 rounded-[12px] p-4 shadow-sm">
                  <textarea
                    rows={showActionButtons ? 3 : 1}
                    maxLength={2000}
                    value={comment.content}
                    onChange={commentChangeHandler}
                    onClick={toggleActionButtons}
                    placeholder="Share your thoughts..."
                    className="w-full bg-transparent border-none p-0 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-0 resize-none transition-all"
                  />
                  {showActionButtons && (
                    <div className="flex justify-end gap-3 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 animate-fadeIn">
                      <button
                        onClick={cancelComment}
                        className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={!comment.content.trim()}
                        onClick={submitComment}
                        className="px-6 py-2 text-sm font-bold bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-blue-500/20"
                      >
                        Post Comment
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments List */}
              <div className="space-y-6">
                {comments && comments.length > 0 ? (
                  <>
                    {/* Show/Hide Toggle */}
                    <div className="flex justify-start">
                      <button
                        onClick={() => setShowComments(!showComments)}
                        className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline decoration-2 underline-offset-2 transition-all"
                      >
                        {showComments ? "Hide Comments" : "Show Comments"}
                      </button>
                    </div>

                    {showComments && (
                      <div className="space-y-4 animate-fadeIn">
                        {comments
                          .toSorted(
                            (a, b) =>
                              new Date(b.postedDate) - new Date(a.postedDate),
                          )
                          .map((comment) => (
                            <EventComment key={comment.id} comment={comment} />
                          ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-slate-400 dark:text-slate-500 italic">
                    No comments yet. Be the first to start the conversation!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <Modal isOpen={showLikeDialog} onClose={setShowLikeDialog}>
        <LocalizedModal
          setOpen={setShowLikeDialog}
          title="Join the conversation"
          message={likeAndDislikeMessage}
          actionText="Sign In"
          cancelText="Not now"
          actionHandler={singIn}
        />
      </Modal>
      <Modal isOpen={showDislikeDialog} onClose={setShowDislikeDialog}>
        <LocalizedModal
          setOpen={setShowDislikeDialog}
          title="Join the conversation"
          message={likeAndDislikeMessage}
          actionText="Sign In"
          cancelText="Not now"
          actionHandler={singIn}
        />
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default EventCard;
